import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subject } from 'rxjs';
import { UserService } from '../core/services/user.service';
import { IUser } from '../core/interfaces/user.interface';
import { EnvService } from './env.service';
import { ICase } from '../modules/cases/interfaces/case.interface';

export interface INotification {
  _id: string;
  user: IUser;
  userAction?: IUser;
  title: string;
  description: string;
  redirectTo: string;
  read: boolean;
  deleted: boolean;
  createdAt: Date;
  updatedAt: Date;
  __v: number;
  case: any;
}

@Injectable({
  providedIn: 'root',
})
export class NotificacionService {
  baseUrl = `${this.env.URL_API_V1}/notification`;

  constructor(
    private http: HttpClient,
    private readonly userService: UserService,
    private env: EnvService
  ) { }

  get user() {
    return this.userService.currentUser;
  }

  private _activeTab = 'all';

  private _casesSelected = [];

  get casesSelected() {
    return this._casesSelected;
  }

  set casesSelected(cases: string[]) {
    this._casesSelected = cases;
    this.updatedNotificationsPage.next();
  }

  get activeTab() {
    return this._activeTab;
  }

  set activeTab(tab: string) {
    this._activeTab = tab;
    this._casesSelected = [];
    this.updatedNotificationsPage.next();
  }

  updatedNotifications = new Subject<string>();
  updatedNotificationsPage = new Subject<string>();
  updateUnreads = new Subject();
  markAllRead = new Subject();

  obtenerNotificaciones(type = 'all', skip: number) {
    return this.http.get<INotification[]>(
      `${this.baseUrl}/user/${this.user._id}`,
      {
        params: {
          skip: skip.toString(),
          type,
          filter: JSON.stringify({
            unit: this.activeTab,
            cases: this.casesSelected,
          }),
        },
      }
    );
  }

  countNotificationsUnread() {
    return this.http.get<number>(`${this.baseUrl}/count/${this.user._id}`);
  }

  changeStatus(read: boolean, id: string) {
    return this.http.put<INotification>(`${this.baseUrl}/changestatus/${id}`, {
      read,
    });
  }

  allAsRead() {
    this.http
      .put(`${this.baseUrl}/allread/${this.user._id}`, null)
      .subscribe(() => {
        this.updateUnreads.next();
        this.markAllRead.next();
      });
  }
  makeAsRead(id: string) {
    this.http
      .put<INotification>(`${this.baseUrl}/changestatus/${id}`, {
        read: true,
      })
      .subscribe({ next: () => this.updateUnreads.next() });
  }

  asignarCaso(especialista_id: string, numero_caso: string) {
    return this.http.get(
      `${this.baseUrl}/notificacion/asignar/${especialista_id}/${numero_caso}`
    );
  }

  desasignarCaso(especialista_id: string, numero_caso: string) {
    return this.http.get(
      `${this.baseUrl}/notificacion/desasignar/${especialista_id}/${numero_caso}`
    );
  }

  solicitudAprobacion(gerente_id: string, numero_caso: string) {
    return this.http.get(
      `${this.baseUrl}/notificacion/solicitud/${gerente_id}/${numero_caso}`
    );
  }

  aprobacion(especialista_id: string, numero_caso: string) {
    return this.http.get(
      `${this.baseUrl}/notificacion/aprobacion/${especialista_id}/${numero_caso}`
    );
  }

  rechazo(especialista_id: string, numero_caso: string, comentario: string) {
    return this.http.get(
      `${this.baseUrl}/notificacion/rechazo/${especialista_id}/${numero_caso}/${comentario}`
    );
  }

  casoPendienteFinalizar(gerente_id: string, numero_caso: string) {
    return this.http.get(
      `${this.baseUrl}/notificacion/pendiente/${gerente_id}/${numero_caso}`
    );
  }

  finalizarCaso(especialista_id: string, numero_caso: string) {
    return this.http.get(
      `${this.baseUrl}/notificacion/finalizado/${especialista_id}/${numero_caso}`
    );
  }

  actualizarModeloNotificacion(notificacion: INotification) {
    return this.http.put(`${this.baseUrl}/notificacion/update`, notificacion);
  }

  eliminarNotificacion(id_notificacion: string) {
    const id_usuario = sessionStorage.getItem('id_usuario');
    return this.http.delete(
      `${this.baseUrl}/notificacion/update/${id_notificacion}/${id_usuario}`
    );
  }

  eliminarNotificaciones(array_notificaciones: INotification[]) {
    const id_usuario = sessionStorage.getItem('id_usuario');
    return this.http.post(`${this.baseUrl}/notificacion/delete`, {
      array_notificaciones,
      id_usuario,
    });
  }

  // Casos Gestión de casos VIP
  solicitudCasoVipAprobacion(gerente_id: string, numero_caso: string) {
    return this.http.get(
      `${this.baseUrl}/notificacion/solicitudCasoVip/${gerente_id}/${numero_caso}`
    );
  }

  // tareasFinalizadas
  notificacionTareasFinalizadas(gerente_id: string, numero_caso: string) {
    return this.http.get(
      `${this.baseUrl}/notificacion/tareasFinalizadas/${gerente_id}/${numero_caso}`
    );
  }

  // PersonalInvolucrado
  notificacionPersonalInvolucrado(
    gerente_id: string,
    numero_caso: string,
    datos
  ) {
    return this.http.put(
      `${this.baseUrl}/notificacion/PersonalInvolucrado/${gerente_id}/${numero_caso}`,
      datos
    );
  }
  // NO hayPersonalInvolucrado
  notificacionNoPersonalInvolucrado(gerente_id: string, numero_caso: string) {
    return this.http.get(
      `${this.baseUrl}/notificacion/NoPersonalInvolucrado/${gerente_id}/${numero_caso}`
    );
  }

  notificacionDeteccionFraudesSolicitarRevisionSupervisor(id_caso: string) {
    return this.http.get(
      `${this.baseUrl}/notificacion/supervisor_fraudes/revision/${id_caso}`
    );
  }

  notificacionDeteccionFraudeAprobadoGerenteASupervisor(id_caso: string) {
    return this.http.get(
      `${this.baseUrl}/notificacion/supervisor_fraudes/revision/aprobada/${id_caso}`
    );
  }

  notificacionDeteccionFraudesSolicitarRevisionGerente(id_caso: string) {
    return this.http.get(
      `${this.baseUrl}/notificacion/gerente_fraudes/revision/${id_caso}`
    );
  }

  // NO hay procedencia del caso
  notificacionNoProcedenciaCaso(gerente_id: string, numero_caso: string) {
    return this.http.get(
      `${this.baseUrl}/notificacion/NoProcedenciaCaso/${gerente_id}/${numero_caso}`
    );
  }
  // NO es valido el documento
  NotificacionNoDocumentoValido(gerente_id: string, numero_caso: string) {
    return this.http.get(
      `${this.baseUrl}/notificacion/NoDocumentoValido/${gerente_id}/${numero_caso}`
    );
  }
  // autorizar y patron
  notificacionAutorizarPatron(
    gerente_id: string,
    numero_caso: string,
    msj: any
  ) {
    return this.http.post(
      `${this.baseUrl}/notificacion/AutorizarPatron/${gerente_id}/${numero_caso}`,
      msj
    );
  }

  // PersonalInvolucrado ROl de guardia
  notificacionPersonalInvolucradoRolDeGuardia(
    gerente_id: string,
    numero_caso: string,
    datos
  ) {
    return this.http.put(
      `${this.baseUrl}/notificacion/PersonalInvolucradoRolDeGuardia/${gerente_id}/${numero_caso}`,
      datos
    );
  }
}
